import { useState } from "react";
import { Link } from "react-router-dom";
import { Play, GraduationCap, Users, Heart, ArrowLeft } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

const TUTORIALS = {
  students: [
    { title: "Getting Started — Pick your Test", duration: "2:30", videoId: "dQw4w9WgXcQ" },
    { title: "How to earn XP and level up", duration: "3:15", videoId: "dQw4w9WgXcQ" },
    { title: "Using Archie the AI tutor", duration: "4:00", videoId: "dQw4w9WgXcQ" },
    { title: "Interactive lessons: tips for success", duration: "3:45", videoId: "dQw4w9WgXcQ" },
  ],
  parents: [
    { title: "Setting up your child's account", duration: "3:00", videoId: "dQw4w9WgXcQ" },
    { title: "Reading the weekly progress report", duration: "3:40", videoId: "dQw4w9WgXcQ" },
    { title: "Choosing the right plan", duration: "2:20", videoId: "dQw4w9WgXcQ" },
  ],
  teachers: [
    { title: "Creating classes and adding students", duration: "5:00", videoId: "dQw4w9WgXcQ" },
    { title: "Assigning reteach packs", duration: "4:15", videoId: "dQw4w9WgXcQ" },
    { title: "Using analytics to guide instruction", duration: "6:00", videoId: "dQw4w9WgXcQ" },
  ],
};

const TABS = [
  { id: "students", label: "For Students", icon: GraduationCap, color: "indigo" },
  { id: "parents", label: "For Parents", icon: Heart, color: "pink" },
  { id: "teachers", label: "For Teachers", icon: Users, color: "emerald" },
];

const Tutorials = () => {
  const { t } = useSettings();
  const [active, setActive] = useState("students");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-12">
        <div className="max-w-5xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <h1 className="font-heading font-black text-3xl sm:text-5xl" data-testid="tutorials-title">
            Video Tutorials
          </h1>
          <p className="text-white/80 mt-2 text-lg">
            Quick guides for students, parents, and teachers.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Tab bar */}
        <div className="flex gap-2 border-b-2 border-slate-200 mb-6 overflow-x-auto" data-testid="tutorials-tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = active === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActive(tab.id)}
                data-testid={`tutorials-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 font-bold whitespace-nowrap border-b-2 -mb-[2px] transition-colors ${
                  isActive
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Video grid */}
        <div className="grid sm:grid-cols-2 gap-6">
          {TUTORIALS[active].map((v, idx) => (
            <div
              key={v.title}
              className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200"
              data-testid={`tutorial-card-${active}-${idx}`}
            >
              {/* Placeholder video embed (YouTube) */}
              <div className="relative w-full pb-[56.25%] bg-slate-900">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${v.videoId}`}
                  title={v.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-4">
                <p className="font-bold text-slate-800">{v.title}</p>
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Play className="w-3 h-3" />
                  {v.duration}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center text-sm text-slate-400">
          <p>More tutorials coming soon. Have a request?{" "}
            <Link to="/contact" className="text-indigo-500 font-bold hover:underline">
              Let us know
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Tutorials;
